/* =========================================================================
   ClueVine — Petal (a cozy hangman)
   Guess the hidden word one letter at a time. Every wrong guess plucks a petal;
   reveal the whole word before they're all gone. Letter-by-letter deduction —
   no position clues, unlike Cipher.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';
import type { TileState } from '../../components/Tile';

export interface PetalConfig {
  length: number;
  maxMisses: number;
}

export interface PetalState {
  answer: string;
  guessed: string[]; // letters tried, in order
  length: number;
  maxMisses: number;
}

export type PetalMove = { type: 'guess'; ch: string };

export const PETAL_DIFFICULTIES: DifficultyLevel<PetalConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '5 letters · 7 petals', scoreMultiplier: 0.9, config: { length: 5, maxMisses: 7 } },
  { id: 'standard', label: 'Garden', blurb: '6 letters · 6 petals', scoreMultiplier: 1, config: { length: 6, maxMisses: 6 } },
  { id: 'hard', label: 'Thicket', blurb: '7 letters · 6 petals', scoreMultiplier: 1.3, config: { length: 7, maxMisses: 6 } },
];

export function misses(state: PetalState): number {
  return state.guessed.filter((l) => !state.answer.includes(l)).length;
}

export function isRevealed(state: PetalState): boolean {
  return state.answer.split('').every((l) => state.guessed.includes(l));
}

/** Keyboard tinting: guessed letters go green (in word) or grey (not). */
export function letterStates(state: PetalState): Record<string, TileState> {
  const map: Record<string, TileState> = {};
  for (const l of state.guessed) map[l] = state.answer.includes(l) ? 'correct' : 'absent';
  return map;
}

export function generate(ctx: GenerateContext<PetalConfig>): PetalState {
  const { length, maxMisses } = ctx.difficulty.config;
  const answer = pickAvoiding(ctx.prng, ctx.dictionary.answersByLength(length), ctx.avoid);
  return { answer, guessed: [], length, maxMisses };
}

export function status(state: PetalState): PuzzleStatus {
  if (isRevealed(state)) return 'won';
  if (misses(state) >= state.maxMisses) return 'lost';
  return 'playing';
}

export function validateMove(state: PetalState, move: PetalMove): { ok: boolean; reason?: string } {
  if (state.guessed.includes(move.ch)) return { ok: false, reason: 'Already tried that' };
  return { ok: true };
}

export function reduce(state: PetalState, move: PetalMove): PetalState {
  if (move.type !== 'guess' || state.guessed.includes(move.ch)) return state;
  return { ...state, guessed: [...state.guessed, move.ch] };
}

export function handleKey(state: PetalState, key: InputKey): PetalMove | null {
  if (key.type !== 'char') return null;
  return state.guessed.includes(key.value) ? null : { type: 'guess', ch: key.value };
}

export function hint(state: PetalState): HintOutcome<PetalState> | null {
  if (status(state) !== 'playing') return null;
  const letter = state.answer.split('').find((l) => !state.guessed.includes(l));
  if (!letter) return null;
  return {
    message: `Revealed the letter “${letter.toUpperCase()}”.`,
    penalty: 40,
    state: { ...state, guessed: [...state.guessed, letter] },
  };
}

export function score(state: PetalState): ModeScore {
  if (status(state) !== 'won') {
    return { base: 0, lines: [{ label: 'Not solved', value: 0, kind: 'base' }], parMs: state.length * 8000 };
  }
  const left = state.maxMisses - misses(state);
  const base = 200 + left * 110 + state.length * 30;
  return {
    base,
    lines: [{ label: `Solved · ${left} petal${left === 1 ? '' : 's'} left`, value: base, kind: 'base' }],
    parMs: state.length * 8000,
    perfect: misses(state) === 0,
  };
}

export function summarize(state: PetalState): ResultSummary {
  const won = status(state) === 'won';
  const m = misses(state);
  return {
    status: won ? 'won' : 'lost',
    headline: won ? (m === 0 ? 'Solved — not a petal lost!' : `Solved with ${m} miss${m === 1 ? '' : 'es'}`) : 'All petals fell',
    answer: state.answer.toUpperCase(),
    attempts: m,
    shareGrid: [won ? `🌸 ${m} miss${m === 1 ? '' : 'es'}` : '🥀 wilted'],
  };
}

export function deserialize(raw: unknown): PetalState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<PetalState>;
  if (typeof s.answer !== 'string' || !Array.isArray(s.guessed) || typeof s.length !== 'number') {
    return null;
  }
  return {
    answer: s.answer,
    guessed: s.guessed.filter((l): l is string => typeof l === 'string'),
    length: s.length,
    maxMisses: typeof s.maxMisses === 'number' ? s.maxMisses : 6,
  };
}
